<script setup lang="ts">
import { storeToRefs } from 'pinia';
import Drawer from 'primevue/drawer';

import { useNotificationsStore } from '../store/notifications.store';
import NotificationsDrawerHeader from './NotificationsDrawerHeader.vue';
import NotificationsDrawerBody from './NotificationsDrawerBody.vue';
import NotificationsDrawerFooter from './NotificationsDrawerFooter.vue';

interface Props {
  visible: boolean;
}

interface Emits {
  'update:visible': [value: boolean];
}

defineProps<Props>();
defineEmits<Emits>();

const notificationsStore = useNotificationsStore();
const { tab, filteredNotifications, unreadCount, isLoadingNotifications } =
  storeToRefs(notificationsStore);
const { setTab, markAsRead, markAllAsRead, fetchNotifications } = notificationsStore;

const handleOpen = () => {
  void fetchNotifications();
};
</script>

<template>
  <Drawer
    :visible="visible"
    position="right"
    :style="{ width: '33rem' }"
    class="notifications-drawer"
    @show="handleOpen"
    @update:visible="$emit('update:visible', $event)"
  >
    <template #container="{ closeCallback }">
      <div class="notifications-drawer__content">
        <NotificationsDrawerHeader
          :tab="tab"
          :unread-count="unreadCount"
          @update:tab="setTab"
          @close="closeCallback"
        />

        <NotificationsDrawerBody
          :notifications="filteredNotifications"
          :is-loading="isLoadingNotifications"
          @mark-as-read="markAsRead"
        />

        <NotificationsDrawerFooter
          :disabled="unreadCount === 0"
          @mark-all-as-read="markAllAsRead"
        />
      </div>
    </template>
  </Drawer>
</template>

<style scoped lang="scss">
/* Drawer root is teleported; style the root element via its class. */
:deep(.p-drawer.notifications-drawer) {
  width: f(360, 420);
  max-width: 100%;
}

.notifications-drawer__content {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--p-surface-0);
}
</style>
